import React, { useState } from "react";
import { Lock, Coins, Frame, Image as ImageIcon, Crown, Stars, Check, Sparkles, Package } from "lucide-react";
import { useTokens } from "../TokenContext";
import { SKIN_LIST, SkinPatternOverlay } from "../skins";
import { toast } from "sonner";
import { useT } from "../../context/LanguageContext";

/**
 * V2 Store — 8 redeemable skins (4 originals + 4 national-team series) plus a
 * row of locked teaser cosmetics (borders + frames) for "Coming Soon" flavor.
 */
const TEASERS = [
  { id: "border-gold",     name: "Gold Border",     category: "Border",       price: 60,  color: "#F59E0B", icon: Frame,     rarity: "common" },
  { id: "border-platinum", name: "Platinum Border", category: "Border",       price: 150, color: "#E5E7EB", icon: Frame,     rarity: "epic" },
  { id: "border-obsidian", name: "Obsidian Border", category: "Border",       price: 180, color: "#1F2937", icon: Frame,     rarity: "legendary" },
  { id: "frame-classic",   name: "Classic Frame",   category: "Avatar Frame", price: 40,  color: "#10B981", icon: ImageIcon, rarity: "common" },
  { id: "frame-champion",  name: "Champion Frame",  category: "Avatar Frame", price: 200, color: "#FCD34D", icon: Crown,     rarity: "legendary" },
  { id: "frame-stardust",  name: "Stardust Frame",  category: "Avatar Frame", price: 110, color: "#A78BFA", icon: Stars,     rarity: "rare" },
];

// Packs — reward bundles, each with its own designed card (see PackCard).
const PACKS = [
  { id: "pack-starter", name: "Starter Pack", tagline: "3 cosmetics", price: 120, color: "#34D399", rarity: "common" },
  { id: "pack-pro",     name: "Pro Pack",     tagline: "5 items · 1 rare guaranteed", price: 300, color: "#60A5FA", rarity: "rare" },
  { id: "pack-legend",  name: "Legend Pack",  tagline: "7 items · 1 legendary", price: 600, color: "#FCD34D", rarity: "legendary" },
];

const RARITY_STYLES = {
  common:    { ring: "ring-[#71717A]/40", chip: "bg-[#27272F] text-[#A1A1AA]" },
  rare:      { ring: "ring-[#3B82F6]/50", chip: "bg-[#3B82F6]/15 text-[#93C5FD]" },
  epic:      { ring: "ring-[#A78BFA]/50", chip: "bg-[#A78BFA]/15 text-[#C4B5FD]" },
  legendary: { ring: "ring-[#FCD34D]/60", chip: "bg-[#FCD34D]/15 text-[#FCD34D]" },
};

export default function StoreV2() {
  const tokens = useTokens();
  const { t } = useT();
  const { balance, total_points, points_per_token, owned_skins, active_skin, redeemSkin, applySkin } = tokens;

  return (
    <div className="space-y-5 px-5 pt-5" data-testid="v2-store">
      {/* Header */}
      <section data-testid="v2-store-header">
        <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#FCD34D]">{t("v2.store.kicker")}</div>
        <h1 className="text-2xl font-bold text-white mt-0.5">{t("v2.store.title")}</h1>
      </section>

      {/* Balance summary */}
      <div className="flex items-center justify-between rounded-2xl border border-[#1F1F2A] bg-[#12121C] px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FFD700] via-[#F59E0B] to-[#B45309] flex items-center justify-center">
            <Coins className="w-4 h-4 text-[#3B2503]" strokeWidth={2.5} />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#71717A]">{t("v2.store.balance")}</div>
            <div className="text-xl font-bold text-[#FCD34D] tabular-nums" data-testid="v2-store-balance">{balance} <span className="text-[10px] text-[#A1A1AA] font-bold uppercase tracking-wider">{t("v2.tok.tokens")}</span></div>
          </div>
        </div>
        <div className="text-right text-[10px] font-mono text-[#71717A]">
          {t("v2.store.career", { n: total_points })}<br /><span className="text-[#52525B]">{t("v2.tok.ratio", { n: points_per_token })} {t("v2.tok.tokens")}</span>
        </div>
      </div>

      {/* Skin catalog — 8 redeemable */}
      <section data-testid="v2-store-skins">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA] mb-2.5">{t("v2.store.skins")}</h3>
        <div className="grid grid-cols-2 gap-3">
          {SKIN_LIST.map((sk) => (
            <SkinCard
              key={sk.id}
              skin={sk}
              owned={owned_skins.includes(sk.id)}
              active={active_skin === sk.id}
              balance={balance}
              onRedeem={redeemSkin}
              onApply={applySkin}
              t={t}
            />
          ))}
        </div>
      </section>

      {/* Packs */}
      <section data-testid="v2-store-packs">
        <div className="flex items-center gap-2 mb-2.5">
          <Package className="w-3.5 h-3.5 text-[#34D399]" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA]">Packs</h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {PACKS.map((p) => <PackCard key={p.id} pack={p} t={t} />)}
        </div>
      </section>

      {/* Coming Soon teasers */}
      <section data-testid="v2-store-teasers">
        <div className="flex items-center gap-2 mb-2.5">
          <Lock className="w-3.5 h-3.5 text-[#FCD34D]" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA]">{t("v2.store.teasers")}</h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {TEASERS.map((it) => <TeaserCard key={it.id} item={it} t={t} />)}
        </div>
      </section>

      <div className="text-center text-[10px] font-mono uppercase tracking-wider text-[#52525B] py-4">
        {t("v2.store.footer", { n: points_per_token })}
      </div>
    </div>
  );
}

function SkinCard({ skin, owned, active, balance, onRedeem, onApply, t }) {
  const [busy, setBusy] = useState(false);
  const Icon = skin.icon;
  const canAfford = balance >= skin.price;
  const styles = RARITY_STYLES[skin.rarity] || RARITY_STYLES.common;
  const skinName = t(`v2.skin.${skin.id}.name`);
  const skinLabel = t(`v2.skin.${skin.id}.label`);
  const skinTag = t(`v2.skin.${skin.id}.tagline`);

  const handleRedeem = async () => {
    if (busy) return;
    setBusy(true);
    const res = await onRedeem(skin.id);
    setBusy(false);
    if (res?.ok) toast.success(t("v2.store.unlocked", { name: skinName }));
    else toast.error(res?.error || t("v2.store.errRedeem"));
  };

  const handleApply = async () => {
    if (busy) return;
    setBusy(true);
    const res = await onApply(active ? null : skin.id);
    setBusy(false);
    if (res?.ok) toast.success(active ? t("v2.store.removed") : t("v2.store.applied", { name: skinName }));
    else toast.error(res?.error || t("v2.store.errApply"));
  };

  return (
    <div
      data-testid={`v2-store-skin-${skin.id}`}
      className={`relative rounded-2xl overflow-hidden ring-1 ${styles.ring} ${active ? "ring-2 !ring-[#FCD34D]" : ""}`}
      style={{
        background: skin.bg,
        border: `1px solid ${skin.border}`,
        boxShadow: skin.shadow,
      }}
    >
      <SkinPatternOverlay skinId={skin.id} />
      <div className="relative aspect-[4/3] flex flex-col items-center justify-center p-3">
        <Icon className="w-10 h-10 mb-1.5" style={{ color: skin.accent }} strokeWidth={1.8} />
        <div className="text-base font-bold uppercase tracking-[0.2em]" style={{ color: skin.text }}>{skinLabel}</div>
        <div className="text-[9px] font-mono uppercase tracking-wider mt-0.5" style={{ color: skin.textMuted }}>{skinTag}</div>
        <span className={`absolute top-1.5 left-1.5 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${styles.chip}`}>
          {skin.rarity}
        </span>
        {active && (
          <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FCD34D] text-[#3B2503]">
            <Check className="w-2.5 h-2.5" /> {t("v2.store.active")}
          </span>
        )}
      </div>
      <div className="relative bg-[#0A0A0F]/60 backdrop-blur p-2.5 border-t" style={{ borderColor: skin.border }}>
        {owned ? (
          <button
            type="button"
            onClick={handleApply}
            disabled={busy}
            data-testid={`v2-store-apply-${skin.id}`}
            className={`w-full text-[11px] font-bold uppercase tracking-wider py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
              active
                ? "bg-[#FCD34D]/15 text-[#FCD34D] hover:bg-[#FCD34D]/25"
                : "bg-[#FCD34D] text-[#3B2503] hover:bg-[#FDE68A]"
            }`}
          >
            {busy ? "…" : (active ? t("v2.store.remove") : t("v2.store.apply"))}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleRedeem}
            disabled={busy || !canAfford}
            data-testid={`v2-store-redeem-${skin.id}`}
            className={`w-full flex items-center justify-center gap-1 text-[11px] font-bold uppercase tracking-wider py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
              canAfford ? "bg-gradient-to-r from-[#F59E0B] to-[#FCD34D] text-[#1A1408] hover:from-[#FBBF24] hover:to-[#FDE68A]" : "bg-[#27272F] text-[#71717A]"
            }`}
            title={!canAfford ? t("v2.store.needMore", { n: skin.price - balance }) : ""}
          >
            <Coins className="w-3 h-3" />
            <span className="tabular-nums">{skin.price}</span>
            <span className="ml-0.5">{busy ? "…" : (canAfford ? t("v2.store.redeem") : t("v2.store.locked"))}</span>
          </button>
        )}
      </div>
    </div>
  );
}

/* Designed reward-pack card — gradient lid, glowing crate icon, rarity chip and
   contents tagline. Dimmed + "Coming Soon" locked until the pack flow ships. */
function PackCard({ pack, t }) {
  const styles = RARITY_STYLES[pack.rarity] || RARITY_STYLES.common;
  return (
    <div
      data-testid={`v2-store-pack-${pack.id}`}
      className={`relative rounded-2xl overflow-hidden ring-1 ${styles.ring} opacity-60`}
      style={{
        border: `1px solid ${pack.color}55`,
        background: `linear-gradient(160deg, ${pack.color}26 0%, #0A0A0F 72%)`,
        boxShadow: `0 8px 28px -12px ${pack.color}55`,
      }}
    >
      {/* ambient glow */}
      <div aria-hidden className="absolute -top-10 left-1/2 -translate-x-1/2 w-32 h-32 rounded-full blur-3xl pointer-events-none" style={{ background: `${pack.color}44` }} />
      <div className="relative aspect-[4/3] flex flex-col items-center justify-center p-3 text-center">
        <span className={`absolute top-1.5 left-1.5 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${styles.chip}`}>
          {pack.rarity}
        </span>
        <div className="relative mb-1.5">
          <div aria-hidden className="absolute inset-0 -m-3 rounded-full blur-xl" style={{ background: `${pack.color}55` }} />
          <Package className="relative w-12 h-12 drop-shadow-lg" style={{ color: pack.color }} strokeWidth={1.6} />
          <Sparkles className="absolute -top-1 -right-2 w-4 h-4" style={{ color: pack.color }} strokeWidth={2} />
        </div>
        <div className="text-[15px] font-extrabold uppercase tracking-[0.14em] text-white leading-tight">{pack.name}</div>
        <div className="text-[9px] font-mono uppercase tracking-wider mt-0.5 text-white/55">{pack.tagline}</div>
        {/* Coming Soon lock overlay */}
        <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px] flex flex-col items-center justify-center gap-1">
          <Lock className="w-5 h-5 text-white/85" strokeWidth={2.5} />
          <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-white/80">Coming Soon</span>
        </div>
      </div>
      <div className="relative bg-[#0A0A0F]/55 backdrop-blur p-2.5 border-t flex items-center gap-1 text-[#FCD34D]" style={{ borderColor: `${pack.color}33` }}>
        <Coins className="w-2.5 h-2.5" />
        <span className="text-[10px] font-bold tabular-nums">{pack.price}</span>
        <span className="text-[8px] uppercase text-[#71717A] ml-auto">{t("v2.tok.soon")}</span>
      </div>
    </div>
  );
}

function TeaserCard({ item, t }) {
  const Icon = item.icon;
  const styles = RARITY_STYLES[item.rarity] || RARITY_STYLES.common;
  return (
    <div
      data-testid={`v2-store-item-${item.id}`}
      className={`relative rounded-2xl bg-[#12121C] border border-[#1F1F2A] overflow-hidden ring-1 ${styles.ring} opacity-60`}
    >
      <div className="aspect-square relative flex items-center justify-center bg-gradient-to-br from-[#1F1F2A] to-[#0A0A0F]">
        <Icon className="w-8 h-8" style={{ color: item.color }} strokeWidth={1.8} />
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center">
          <Lock className="w-5 h-5 text-white/80" strokeWidth={2.5} />
        </div>
        <span className={`absolute top-1.5 left-1.5 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${styles.chip}`}>
          {item.rarity}
        </span>
      </div>
      <div className="p-2">
        <div className="text-[10px] font-bold text-white truncate">{item.name}</div>
        <div className="flex items-center gap-1 mt-0.5 text-[#FCD34D]">
          <Coins className="w-2.5 h-2.5" />
          <span className="text-[10px] font-bold tabular-nums">{item.price}</span>
          <span className="text-[8px] uppercase text-[#71717A] ml-auto">{t("v2.tok.soon")}</span>
        </div>
      </div>
    </div>
  );
}
